import React from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import iconScreenshot from '../../../../assets/img_icon_image.jpg';
import BoldFontScrrenshot from '../../../../assets/img_bold_font_image.jpg';
import regularFontScreenshot from '../../../../assets/img_regular_font_image.jpg';
import lightFontScreenshot from '../../../../assets/img_light_font_image.jpg';
import FontUpload, { type IFileUploadInstance } from './FileUpload';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import OpenAPI from '../../../../endpoint/Openapi';

import { type components } from '../../../../endpoint/Schema';
import { type CustomError } from '../../../../models/Error';
import MyAccordion from './MyAccordion';

interface IFont {
  title: string;
  appFontType: components['schemas']['AppFontType'];
  description: string;
}

const APPFONTS: IFont[] = [
  {
    title: 'Light',
    appFontType: 'Light',
    description:
      'If Light Font is not provided, the Driverly font will be used.',
  },
  {
    title: 'Regular',
    appFontType: 'Regular',
    description:
      'If Regular Font is not provided, the Driverly font will be used.',
  },
  {
    title: 'Bold',
    appFontType: 'Bold',
    description:
      'If Bold Font screen image is not provided, the Driverly font will be used',
  },
];

function Fonts() {
  const { setTenantConfig } = useTenantStore();
  const tenantId = useTenantStore(selectTenantId);
  const [expandedIndex, setExpandedIndex] = React.useState<number>();
  const [screenshot, setScreenshot] = React.useState(iconScreenshot);
  const [pending, setPending] = React.useState(false);
  const toast = React.useRef<Toast>(null);
  const fontsRef = React.useRef<Array<IFileUploadInstance | null>>([]);

  const handleUpload = async (
    imageUpload: IFileUploadInstance | null,
    title: string,
  ) => {
    try {
      if (imageUpload?.isFileSelected()) {
        const res = await imageUpload?.save();

        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        if (res.error) throw res.error;

        toast.current?.show({
          severity: 'info',
          summary: 'Success',
          detail: `${title} uploaded`,
        });
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }
  };

  const handleSave = async () => {
    setPending(true);

    await Promise.all(
      APPFONTS.map(async (fnt, index) => {
        if (fontsRef.current[index]) {
          await handleUpload(fontsRef.current[index], fnt.title);
        }
      }),
    );

    try {
      const { data } = await OpenAPI.GET('/api/v2/admin/tenant', {
        params: {
          query: {
            tenantId,
          },
        },
      });
      if (data) setTenantConfig(data);
    } catch {}

    setPending(false);
  };

  function onToggle(index: number) {
    setExpandedIndex((prev) => (index === prev ? undefined : index));

    switch (index) {
      case 0:
        setScreenshot(lightFontScreenshot);
        break;
      case 1:
        setScreenshot(regularFontScreenshot);
        break;
      case 2:
        setScreenshot(BoldFontScrrenshot);
        break;
    }
  }

  function renderItem(fnt: IFont, index: number) {
    return (
      <div key={`fnt_${fnt.appFontType}_${index}`} className="max-w-md">
        <MyAccordion
          index={index}
          expandedIndex={expandedIndex}
          onToggle={onToggle}
          title={fnt.title}
          appFontType={fnt.appFontType}
        >
          <div className="flex flex-row">
            <FontUpload
              ref={(el) => (fontsRef.current[index] = el)}
              appFontsType={fnt.appFontType}
            />
          </div>

          <div className="mt-5 leading-7 text-zinc-500">{fnt.description}</div>
        </MyAccordion>
      </div>
    );
  }

  return (
    <div>
      <div className="gap-4">
        <div className="flex flex-row w-full items-start gap-4 max-md:flex-col max-md:gap-8 max-md:items-center">
          <div className="flex flex-col mr-4 w-[50%] max-xl:w-[90%] max-w-[512px] bg-zinc-700 rounded-3xl max-md:mr-0 max-md:w-full max-md:max-w-[320px]">
            <img src={screenshot} />
          </div>

          <div className="flex flex-col justify-center w-full px-6 py-8 rounded-3xl shadow-[0px_4px_12px_rgba(0,0,0,0.1)] text-base leading-6 text-black bg-white">
            <div className="text-3xl font-medium mb-6">Fonts</div>
            <div className=" mb-4">
              Upload font file by opening the accordions
            </div>

            <div className="flex flex-row flex-wrap w-full gap-x-4 gap-y-8">
              {APPFONTS.map(renderItem)}
            </div>
          </div>
        </div>

        <div className="flex flex-row w-full px-6 py-6 justify-end">
          <Button
            label="Save"
            className={`text-2xl px-14 py-3 bg-secondary hover:bg-bluegray-400 text-black border-none`}
            onClick={handleSave}
            loading={pending}
          />
        </div>
      </div>

      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default Fonts;
